import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import React from "react";
import { HOST_API } from "src/config";
import { useSnackbar } from "notistack";
import axiosInstance from "src/utils/axios";
import { PATH_USER } from "./paths";
import Logo from "src/components/logo";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import { Box, Button, Card, CircularProgress, Typography } from "@mui/material";
import { use } from "react";
import useAuth from "src/hooks/useAuth";

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { user, getUser } = useAuth();
  const checkoutId = localStorage.getItem("yoco_checkout_id");
  useEffect(() => {
    if (!checkoutId) {
      setStatus("invalid");
      return;
    }

    const verifyPayment = async () => {
      const reqData = new FormData();
      reqData.append("checkout_id", checkoutId);
      try {
        const { status, data } = await axiosInstance.post(
          `api/yoco/verify-payment`,
          reqData
        );

        if (status === 200) {
          enqueueSnackbar(data.message);
          setStatus("success");
          setPaymentData(data);
          getUser();
          navigate(PATH_USER.my_orders.view(data?.invoice_id), {
            replace: true,
          });
        }
      } catch (error) {
        enqueueSnackbar(error.message || "Something went wrong", {
          variant: "error",
        });
      }
    };

    verifyPayment();
  }, [checkoutId]);

  return (
    <div style={{ padding: 30 }}>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 3,
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 480,
            p: 4,
            textAlign: "center",
            borderRadius: 4,
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
            animation: "fadeIn 0.5s ease-in-out",
          }}
        >
          {/* LOGO */}
          <Box mb={2}>
            <Logo />
          </Box>

          {/* STATUS: LOADING */}
          {status === "loading" && (
            <>
              <HourglassBottomIcon sx={{ fontSize: 70, color: "#1976d2" }} />
              <Typography variant="h5" fontWeight={600} mt={2}>
                Verifying your payment...
              </Typography>
              <CircularProgress sx={{ mt: 2 }} />
            </>
          )}

          {/* STATUS: SUCCESS */}
          {status === "success" && (
            <>
              <CheckCircleIcon sx={{ fontSize: 80, color: "green" }} />
              <Typography variant="h4" fontWeight={700} mt={2} color="green">
                Payment Successful 🎉
              </Typography>
              <Typography mt={1} color="text.secondary">
                Thank you! Your payment has been confirmed.
              </Typography>

              <Box
                mt={3}
                sx={{
                  background: "#f1fff1",
                  p: 2,
                  borderRadius: 2,
                  textAlign: "left",
                  maxHeight: 200,
                  overflow: "auto",
                  fontSize: "14px",
                  border: "1px solid #c8e6c9",
                }}
              >
                <pre>{JSON.stringify(paymentData, null, 2)}</pre>
              </Box>

              <Button
                variant="contained"
                sx={{ mt: 3, borderRadius: 3, textTransform: "none" }}
                href="/user/dashboard"
              >
                Go back to dashboard
              </Button>
            </>
          )}

          {/* STATUS: FAILED */}
          {status === "failed" && (
            <>
              <ErrorIcon sx={{ fontSize: 80, color: "red" }} />
              <Typography variant="h4" fontWeight={700} mt={2} color="red">
                Payment Failed
              </Typography>
              <Typography mt={1} color="text.secondary">
                Your payment could not be verified.
              </Typography>
            </>
          )}

          {/* STATUS: INVALID */}
          {status === "invalid" && (
            <>
              <WarningIcon sx={{ fontSize: 80, color: "#ff9800" }} />
              <Typography variant="h4" fontWeight={700} mt={2}>
                Invalid Request
              </Typography>
              <Typography mt={1} color="text.secondary">
                No checkoutId received.
              </Typography>
            </>
          )}

          {/* STATUS: ERROR */}
          {status === "error" && (
            <>
              <WarningIcon sx={{ fontSize: 80, color: "#f44336" }} />
              <Typography variant="h4" fontWeight={700} mt={2}>
                Server Error
              </Typography>
              <Typography mt={1} color="text.secondary">
                Unable to verify your payment. Please contact support.
              </Typography>
            </>
          )}
        </Card>
      </Box>
    </div>
  );
};

export default PaymentSuccess;
